import { ComponentConfig } from '@measured/puck';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { PaddingProps, paddingFields } from '../../puck/reusable-props/padding';
import SavingIcon from '../../../assets/svg/savings-logo';
import { ReduxSelectorProps } from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import WithAccordion from '../customComponents/CustomAccordion';
import Switch from '@mui/material/Switch';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';

import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';

export type SavingCalculatorProps = {
  placeholder: string;
  background: {
    color: string;
    isInherit: boolean;
  };
  border: IBorderValueProps;
  showSVG: {
    showIcon: boolean;
    iconColor: string;
  };
} & showComponentProps &
  MarginProps &
  PaddingProps;

export const SavingCalculator = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<SavingCalculatorProps> => {
  return {
    label: (
      <CustomTooltip title="SavingCalculator">
        <span>Saving Calculator</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },
      ...backgroungFields,
      placeholder: {
        ...QuillTextEditor('Placeholder').content,
      },
      showSVG: {
        type: 'custom',
        label: 'SVG Customization',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="SVG Customization">
              <div>
                <label>Show Savings Icon</label>
                <Switch
                  checked={value?.showIcon ?? true}
                  onChange={e =>
                    onChange({
                      ...value,
                      showIcon: e.target.checked,
                    })
                  }
                />

                <div className="mt-2">
                  {value.showIcon && (
                    <ColorPicker
                      value={value.iconColor}
                      onChange={color =>
                        onChange({
                          ...value,
                          iconColor: color,
                        })
                      }
                      label="Savings Icon Color"
                      tooltip="savingsIconColor"
                    />
                  )}
                </div>
              </div>
            </WithAccordion>
          );
        },
      },
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      showSVG: {
        showIcon: true,
        iconColor: '#000000',
      },
      margin: {
        desktop: {
          top: '4',
          bottom: '4',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '4',
          bottom: '4',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '2',
          bottom: '2',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      placeholder:
        '<p style="line-height:1.5;"><span style="font-size: 16px;">Total Savings {total_savings}</span></p>',

      ...borderProps,
    },
    render: ({
      margin,
      border,
      padding,
      background,
      showComponent,
      placeholder,
      editMode,
      showSVG,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);

      const state = useAppSelector(state => state);
      const newInterpolation = getQuillEditorText(placeholder, state);
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              style={{
                ...marginStyles,
                ...getPaddingStyles(padding, currentDeviceType),
                ...getBorderProps(border, currentDeviceType),
              }}
              className={`flex items-center space-x-2 ${border?.borderStyle?.[currentDeviceType]} text-editor-content`}>
              {showSVG?.showIcon && (
                <SavingIcon className="h-6 w-6" color={showSVG?.iconColor} />
              )}
              <div
                style={{
                  backgroundColor: background?.isInherit
                    ? 'inherit'
                    : background?.color,
                }}>
                <div
                  className={`inline`}
                  dangerouslySetInnerHTML={{ __html: newInterpolation }}
                />
              </div>
            </div>
          )}
        </>
      );
    },
  };
};
